import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import type { EdpRecipientView, EdpReleasedPackageSummary } from '../../api/employeeDocsTypes.js';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../../components/ui.js';
import { formatDateTime } from '../../lib/format.js';
import { ReleasedPackageView } from './ReleasedPackageView.js';

/** Signed-in USPS Contracting Officer: the packages VGS has released, nothing earlier. */
export function UspsReleasedDocsListPage() {
  const list = useQuery({
    queryKey: ['edp', 'usps-released'],
    queryFn: () =>
      apiRequest<{ packages: EdpReleasedPackageSummary[] }>('/api/usps-released-documents'),
  });

  return (
    <div>
      <PageHeader
        title="Employee documents"
        subtitle="Background check packages VGS quality review has approved and released to USPS. Packages still with the employee or under VGS review are not shown."
      />
      <Card>
        {list.isLoading ? (
          <Spinner label="Loading packages" />
        ) : list.isError ? (
          <Alert variant="error">The packages could not be loaded.</Alert>
        ) : !list.data || list.data.packages.length === 0 ? (
          <EmptyState
            title="No released packages"
            description="Packages appear here once VGS releases them to USPS."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Employee</th>
                  <th className="px-3 py-2">Package</th>
                  <th className="px-3 py-2">Approved by VGS</th>
                  <th className="px-3 py-2">Released to USPS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.packages.map((pkg) => (
                  <tr key={pkg.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link
                        to={`/employee-documents/${pkg.id}`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {pkg.employeeName}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{pkg.title}</td>
                    <td className="px-3 py-2">{formatDateTime(pkg.approvedAt)}</td>
                    <td className="px-3 py-2">{formatDateTime(pkg.releasedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function UspsReleasedDocsPackagePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const view = useQuery({
    queryKey: ['edp', 'usps-released', id],
    queryFn: () => apiRequest<EdpRecipientView>(`/api/usps-released-documents/${id}`),
    retry: false,
  });

  return (
    <div>
      <PageHeader
        title="Background check package"
        actions={
          <Link
            to="/employee-documents"
            className="rounded-md px-3.5 py-2 text-sm font-semibold text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
          >
            All released packages
          </Link>
        }
      />
      {view.isLoading ? (
        <Spinner label="Loading the package" />
      ) : view.isError || !view.data ? (
        <Alert variant="error" title="Package not available">
          This package has not been released to USPS or no longer exists.
        </Alert>
      ) : (
        <ReleasedPackageView
          view={view.data}
          fileUrl={(fileId, inline) =>
            `/api/usps-released-documents/${id}/files/${fileId}${inline ? '?inline=1' : ''}`
          }
          archiveUrl={`/api/usps-released-documents/${id}/archive`}
          acceptUrl={`/api/usps-released-documents/${id}/accept`}
          returnUrl={`/api/usps-released-documents/${id}/return`}
          onChanged={async () => {
            await view.refetch();
          }}
          onReturned={async () => {
            await queryClient.invalidateQueries({ queryKey: ['edp', 'usps-released'] });
            navigate('/employee-documents', { replace: true });
          }}
        />
      )}
    </div>
  );
}
